// React and hooks
import React, { useEffect, useMemo, useState, useCallback } from "react";

// Kendo UI
import { PDFViewer } from "@progress/kendo-react-pdf-viewer";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Loader } from "@progress/kendo-react-indicators";
import { SplitButton } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";

// App components
import ShareDocumentModal from "pages/CaseFile/ShareDocumentModal";
import EmailViewer from "../EmailViewer";
import { Tile } from "components/UIComponents/Tile";
import AISummaryModal from "./AISummaryModal";
import EmailToolbar from "./EmailToolbar";

// API
import {
  getDocument,
  getDocumentVerions,
  getEmailDocumentBody,
  getEmailFileBody,
} from "api";
import {
  getFileIcon,
  getFileExtension,
  wordExt,
  excelExt,
  imageExt,
  formatBytes,
} from "./fileUtils";
import {
  buildEmailComposeData,
  openOutlookCompose,
} from "./emailActions";
import {
  ErrorMessage,
  IconBackground,
  VersionCard,
  DialogTitleBarContent,
  EmptyDocument,
} from "./DocumentViewerComponents";

// Icons
import { LiaEditSolid } from "react-icons/lia";
import { IoCaretBack, IoCaretForward } from "react-icons/io5";
import { BiExpand } from "react-icons/bi";
import { FaRegShareFromSquare } from "react-icons/fa6";
import { TbVersionsFilled } from "react-icons/tb";
import { RxCross2 } from "react-icons/rx";
import { AiOutlineDownload } from "react-icons/ai";
import { HiOutlineSparkles } from "react-icons/hi2"; // AI Summary icon
import {
  arrowRotateCwIcon as refreshIcon,
  clipboardHtmlIcon,
} from "@progress/kendo-svg-icons";
import { SvgIcon } from "@progress/kendo-react-common";

// Assets
import closeSideBar from "../../../assets/collapse.png";
import openSideBar from "../../../assets/expand.png";
import NewTabIcon from "../../../assets/CaseFileIcons/NewTabIcon.svg";

// Styles
import { toast } from "react-toastify";
import styles from "./DocumentViewer.module.scss";

export default function DocumentViewer({
  onScreenComponent,
  data,
  onClose,
  onExpand,
  iconLess,
  onRefresh,
  onDownload,
  matterId,
  matter,
  saveDocuemntEdit,
  onEditDoc,
  OnlyRefreshEditIcon,
  showTools = false,
  hideToolbar,
  openedFromPreview = false,
  isStandalone = false,
  matterRef,
  actionBar = true,
  setDetails,
  activityType,
  details,
  onOpenInNewTab,
  component,
}) {
  const [loadingScreen, setLoadingScreen] = useState(false);
  const [fileNo, setFileNo] = useState(0);
  const [fileName, setFileName] = useState([]);
  const [fileUrl, setFileUrl] = useState([]);
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(0);
  const [size, setSize] = useState([]);
  const [collapsedVersion, setCollapsedVersion] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);

  const isBatchPosting = component === "batchPosting";

  const [showShareDocument, setShowShareDocument] = useState(false);
  const [showAISummary, setShowAISummary] = useState(false);
  const [currentDoc, setCurrentDoc] = useState({});

  const isEmpty = fileName.length === 0;
  const [pdfError, setPdfError] = useState(false);
  const [officeViewerLoaded, setOfficeViewerLoaded] = useState(false);
  useEffect(() => {
    if (data) {
      handleFileData(data);
      setPdfError(false);
      setFileNo(0);
      setOfficeViewerLoaded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (isStandalone) {
      setFullScreen(true);
    }
  }, [isStandalone]);

  const handleDocumentRefresh = useCallback(async () => {
    const documentId = data?.[fileNo]?.documentId;
    if (!documentId) return;

    setLoadingScreen(true);
    try {
      await onRefresh(documentId);
    } catch (error) {
    } finally {
      setLoadingScreen(false);
    }
  }, [onRefresh, data, fileNo]);

  const showDocumentFile = async (id, specificVersion = false) => {
    const { data } = await getDocument(id, specificVersion);
    handleFileData([data.data]);
  };

  const handleFileData = (dataToProcess) => {
    let fileNameArray = [];
    let fileSizeArray = [];
    let fileUrlArray = [];
    // Reset state if data is empty or undefined
    if (!dataToProcess || dataToProcess.length === 0) {
      setFileUrl([]);
      setSize([]);
      setFileName([]);
      setFileNo(0);
      if (loadingScreen) setLoadingScreen(false);
      return;
    }

    // Email/msg files show placeholder when loading, word/excel show their own loading text
    const needsLoading = dataToProcess.some((file) => {
      if (!file?.documentFile?.downloadURL && !file?.downloadURL) return false;
      const ext = getFileExtension(file.documentName || file.filename);
      return ext === "txt" || imageExt.includes(ext);
    });

    if (needsLoading) {
      setLoadingScreen(true);
    }

    dataToProcess?.forEach((file, index) => {
      if (file?.documentFile?.downloadURL || file?.downloadURL) {
        let ext = getFileExtension(file.documentName || file.filename);

        switch (ext) {
          case "txt":
            createBase64Format(
              file.documentFile?.downloadURL || file?.downloadURL,
              index
            );
            break;
          case "msg":
          case "eml":
            getEmail(file, index);
            break;
          default:
        }

        let fn = file.documentName || file.filename;
        let size = file.documentFile?.size || file.size;
        fileNameArray.push(fn);
        fileSizeArray.push(size);
        if (
          ext === "pdf" ||
          wordExt.includes(ext) ||
          excelExt.includes(ext) ||
          imageExt.includes(ext)
        ) {
          fileUrlArray.push(file.documentFile?.downloadURL || file?.downloadURL);
        } else {
          fileUrlArray.push(null);
        }
      } else {
        toast.error("Invalid document data");
      }
    });
    setFileUrl(fileUrlArray);
    setSize(fileSizeArray);
    setFileName(fileNameArray);
  };

  const splitButtonItems = useMemo(() => {
    const ext = fileName[fileNo]?.split(".").pop()?.toLowerCase();
    const isExcelFile = excelExt.includes(ext);

    return [
      {
        text: "Edit in browser",
        svgIcon: clipboardHtmlIcon,
        id: 0,
      },
      {
        text: isExcelFile ? "Edit in Excel" : "Edit in Word",
        svgIcon: clipboardHtmlIcon,
        id: 1,
      },
      {
        text: "Finish Editing",
        svgIcon: clipboardHtmlIcon,
        id: 2,
      },
    ];
  }, [fileName, fileNo]);

  const downloadButtonItems = useMemo(() => {
    const ext = fileName[fileNo]?.split(".").pop()?.toLowerCase();

    const baseItem = {
      text: "Download file",
      svgIcon: clipboardHtmlIcon,
      id: 0,
    };

    if (ext !== "pdf") {
      return [
        baseItem,
        {
          text: "Download as PDF",
          svgIcon: clipboardHtmlIcon,
          id: 1,
        },
      ];
    }

    return [baseItem];
  }, [fileName, fileNo]);

  const pdfViewerTools = useMemo(() => {
    if (!showTools) return [];
    return [
      "pager",
      "spacer",
      "zoomInOut",
      "zoom",
      "selection",
      "spacer",
      "search",
    ];
  }, [showTools]);

  const handleEmailAction = useCallback(
    (action) => {
      const emailInfo = fileUrl[fileNo];

      if (!emailInfo) {
        toast.error("Email is still loading. Please try again.");
        return;
      }

      if (action === "reply" && !emailInfo.fromRecipient?.emailAddress) {
        toast.error("Cannot reply — sender address is missing.");
        return;
      }

      const composeData = buildEmailComposeData(emailInfo, action);

      openOutlookCompose({
        to: composeData.to,
        subject: composeData.subject,
        body: composeData.body,
      });
    },
    [fileUrl, fileNo]
  );

  const getFileVersions = async (file) => {
    try {
      const data = await getDocumentVerions(file.documentId);

      // Sort versions by dateModified in descending order
      const sortedVersions = data?.data?.data?.sort(
        (a, b) => new Date(b.dateModified) - new Date(a.dateModified)
      );

      setVersions(sortedVersions);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!openedFromPreview) return;
    if (data?.length && data[fileNo]) {
      getFileVersions(data[fileNo]);
    }
  }, [data, fileNo, openedFromPreview]);

  useEffect(() => {
    if (versions.length === 1) {
      setCollapsedVersion(true);
    }
  }, [versions]);

  const createBase64Format = async (downloadURL, fileIndex) => {
    try {
      const response = await fetch(downloadURL);
      const blob = await response.blob();
      const textData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsText(blob);
        reader.onload = () => {
          resolve(reader.result);
        };
        reader.onerror = (error) => {
          reject(error);
        };
      });

      setFileUrl((prevFileUrl) => {
        const updatedFileUrl = [...prevFileUrl];
        updatedFileUrl[fileIndex] = textData;
        // Plain text content renders synchronously, so we clear loading here
        if (fileIndex === fileNo) {
          setLoadingScreen(false);
        }

        return updatedFileUrl;
      });
    } catch (error) {
      // Set error state for this specific file
      setFileUrl((prevFileUrl) => {
        const updatedFileUrl = [...prevFileUrl];
        updatedFileUrl[fileIndex] = null;
        return updatedFileUrl;
      });
    }
  };

  const getEmail = async (payload, fileIndex) => {
    const documentId =
      payload?.documentId ??
      payload?.documentID ??
      payload?.documentFile?.documentID;
    const fileId = payload?.fileID;

    if (!documentId && !fileId) {
      setFileUrl((prevFileUrl) => {
        const updatedFileUrl = [...prevFileUrl];
        updatedFileUrl[fileIndex] = null;
        return updatedFileUrl;
      });
      return;
    }

    try {
      let response;

      if (documentId) {
        response = await getEmailDocumentBody(documentId);
      } else if (fileId) {
        response = await getEmailFileBody(fileId);
        if (setDetails) setDetails(response?.data?.data?.subject);
      }

      setFileUrl((prevFileUrl) => {
        const updatedFileUrl = [...prevFileUrl];
        updatedFileUrl[fileIndex] = response?.data?.data;
        return updatedFileUrl;
      });
    } catch (error) {
      console.error("Error loading email", error);
      setFileUrl((prevFileUrl) => {
        const updatedFileUrl = [...prevFileUrl];
        updatedFileUrl[fileIndex] = null;
        return updatedFileUrl;
      });
    }
  };

  const handleFileNo = (action) => {
    if (action === "NEXT") {
      if (fileName.length > fileNo + 1) {
        setFileNo((prev) => {
          const nextIndex = prev + 1;
          setPdfError(false);
          setOfficeViewerLoaded(false);
          return nextIndex;
        });
      }
    }
    if (action === "PREV") {
      if (fileNo !== 0) {
        setFileNo((prev) => {
          const prevIndex = prev - 1;
          setPdfError(false);
          setOfficeViewerLoaded(false);
          return prevIndex;
        });
      }
    }
  };

  const getEditButtonItems = () => {
    if (data[fileNo] && data[fileNo]?.documentFile?.inEditMode) {
      return splitButtonItems;
    } else {
      return splitButtonItems.filter((item) => item.id !== 2);
    }
  };

  const renderFile = () => {
    const ext = getFileExtension(fileName[fileNo]);

    switch (ext) {
      case "pdf":
        if (pdfError) {
          return <ErrorMessage />;
        }

        const fileUrlValue = fileUrl[fileNo];

        if (
          !fileUrlValue ||
          (typeof fileUrlValue === "string" && fileUrlValue.trim().length === 0)
        ) {
          return null;
        }

        return (
          <div
            className={`docviewer h-100 w-100 ${styles["pdf-viewer-container"]}
                        ${!showTools && "pdf-toolbar-hidden"}
                        ${isStandalone && styles["search-settings"]}`}>
            <PDFViewer
              key={`${fileNo}-${fileUrl[fileNo]}`}
              defaultZoom={onScreenComponent ? 0.8 : 1}
              tools={pdfViewerTools}
              onLoad={() => {
                setLoadingScreen(false);
                setPdfError(false);
              }}
              onError={(error) => {
                setLoadingScreen(false);
                setPdfError(true);
                toast.error(
                  "Error loading PDF document. The document may be corrupted or unavailable."
                );
              }}
              url={fileUrl[fileNo]}
              className="w-100 h-100"
            />
          </div>
        );
      case "txt":
        const txtContent = fileUrl[fileNo];

        return (
          <div className="w-100 h-100">
            {txtContent && typeof txtContent === "string" ? (
              <pre className={`w-100 h-100 ${styles["text-file-content"]}`}>
                {txtContent}
              </pre>
            ) : null}
          </div>
        );
      case "doc":
      case "docx":
      case "xls":
      case "xlsx":
        const officeFileExt = fileName[fileNo]?.split(".").pop()?.toLowerCase();
        const overlayColorClass =
          excelExt.includes(officeFileExt)
            ? styles["hide-fullscreen-overlay-excel"]
            : styles["hide-fullscreen-overlay-doc"];

        const handleOfficeIframeLoad = () => {
          // Delay overlay visibility to ensure toolbar has rendered
          setTimeout(() => {
            setOfficeViewerLoaded(true);
            setLoadingScreen(false);
          }, 600);
        };

        return (
          <div className="position-relative w-100 h-100">
            <iframe
              title="Viewer"
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                fileUrl[fileNo]
              )}`}
              width="100%"
              height="100%"
              className="border-0"
              onLoad={handleOfficeIframeLoad}>
              This is an embedded{" "}
              <a target="_blank" rel="noreferrer" href="http://office.com">
                Microsoft Office
              </a>{" "}
              document, powered by{" "}
              <a target="_blank" rel="noreferrer" href="http://office.com/webapps">
                Office Online
              </a>
              .
            </iframe>

            {/*Hide fullscreen option - only show after iframe and toolbar have loaded*/}
            {officeViewerLoaded && !loadingScreen && (
              <div
                className={`position-absolute bottom-0 end-0 ${styles["hide-fullscreen-overlay"]} ${overlayColorClass}`}></div>
            )}
          </div>
        );
      case "jpeg":
      case "png":
      case "gif":
      case "jpg":
        return (
          <div
            className={`d-flex justify-content-center w-100 align-items-center ${styles["image-container"]}`}>
            <img
              className={`${styles["image-responsive"]} ${
                onScreenComponent
                  ? styles["image-on-screen"]
                  : styles["image-standalone"]
              }`}
              src={fileUrl[fileNo]}
              alt="preview"
              onLoad={() => {
                setLoadingScreen(false);
              }}
              onError={() => {
                setLoadingScreen(false);
                toast.error("Failed to load image");
              }}
            />
          </div>
        );
      case "eml":
      case "msg":
        return (
          <div className={`d-flex flex-column h-100 w-100 ${styles["viewer-content"]}`}>
            {showTools && (
              <EmailToolbar
                disabled={!fileUrl[fileNo]}
                onReply={() => handleEmailAction("reply")}
                onForward={() => handleEmailAction("forward")}
              />
            )}
            {!fileUrl[fileNo] ? (
              <div className="d-flex flex-column flex-grow-1 justify-content-center align-items-center">
                <img
                  width={150}
                  height={150}
                  src={getFileIcon(fileName[fileNo])}
                  alt="Document Icon"
                />
                <span className={`mt-2 text-center ${styles["file-name"]}`}>
                  {fileName[fileNo]}
                </span>
                <span className={`mt-2 text-center ${styles["file-size"]}`}>
                  {formatBytes(size[fileNo])}
                </span>
              </div>
            ) : (
              <div className={`flex-grow-1 overflow-hidden ${styles["viewer-content"]}`}>
                <EmailViewer emailInfo={fileUrl[fileNo]} />
              </div>
            )}
          </div>
        );

      default:
        return (
          <div
            className={`d-flex flex-column w-100 justify-content-center align-items-center ${styles["default-document-container"]}`}>
            <img
              width={150}
              height={150}
              src={getFileIcon(fileName[fileNo])}
              alt="Document Icon"
            />
            <span className={`mt-2 text-center ${styles["file-name"]}`}>
              {fileName[fileNo]}
            </span>
            <span className={`mt-2 text-center ${styles["file-size"]}`}>
              {formatBytes(size[fileNo])}
            </span>
          </div>
        );
    }
  };

  const getFileToolBarData = () => {
    if (isBatchPosting) {
      return null;
    }
    return (
      <div className="d-flex align-items-center flex-wrap">
        {!isEmpty && (
          <>
            {downloadButtonItems.length > 1 ? (
              <Tooltip anchorElement="target" position="bottom" parentTitle>
                <span title="Download" className="d-inline-flex">
                  <SplitButton
                    fillMode={"flat"}
                    className={`me-2 ${styles["spliter-class"]}`}
                    text={
                      <div className="d-flex gap-2 align-items-center">
                        <AiOutlineDownload size={24} />
                        {!onScreenComponent && <div>Download</div>}
                      </div>
                    }
                    items={downloadButtonItems}
                    onItemClick={(e) => {
                      if (e?.item?.id === 0) {
                        onDownload(data[fileNo]?.documentId, false, true);
                      } else if (e?.item?.id === 1) {
                        onDownload(data[fileNo]?.documentId, true, true);
                      }
                    }}
                    onButtonClick={(e) =>
                      onDownload(data[fileNo]?.documentId, false, true)
                    }
                  />
                </span>
              </Tooltip>
            ) : (
              <Tooltip anchorElement="target" position="bottom" parentTitle>
                <span title="Download" className="d-inline-flex">
                  <button
                    className={`me-2  ${styles["download-button"]}`}
                    onClick={() => onDownload(data[fileNo]?.documentId, false, true)}>
                    <div
                      className={`${
                        !onScreenComponent && "d-flex gap-2 align-items-center"
                      }`}>
                      <AiOutlineDownload size={24} />
                      {!onScreenComponent && <div>Download</div>}
                    </div>
                  </button>
                </span>
              </Tooltip>
            )}

            {(wordExt.includes(fileName[fileNo]?.split(".").pop()?.toLowerCase()) ||
              excelExt.includes(
                fileName[fileNo]?.split(".").pop()?.toLowerCase()
              )) && (
              <Tooltip anchorElement="target" position="bottom" parentTitle>
                <span title="Edit" className="d-inline-flex">
                  <SplitButton
                    fillMode={"flat"}
                    className={`me-2 ${styles["spliter-class"]}`}
                    text={
                      <div className="d-flex gap-2 align-items-center">
                        <LiaEditSolid size={22} />
                        {!onScreenComponent && <div>Edit</div>}
                      </div>
                    }
                    items={getEditButtonItems()}
                    onItemClick={(e) => {
                      if (e?.item?.id === 0) {
                        onEditDoc(data[fileNo]?.documentId, "Browser");
                      } else if (e?.item?.id === 1) {
                        onEditDoc(data[fileNo]?.documentId, "Local");
                      } else if (e?.item?.id === 2) {
                        saveDocuemntEdit(data[fileNo]?.documentId);
                      }
                    }}
                    onButtonClick={(e) => onEditDoc(data[fileNo]?.documentId, "Local")}
                  />
                </span>
              </Tooltip>
            )}
            <Tooltip anchorElement="target" position="bottom" parentTitle>
              <span title="Share" className="d-inline-flex">
                <button
                  className={`me-2 ${styles["download-button"]} ${
                    !onScreenComponent
                      ? styles["button-padding-normal"]
                      : styles["button-padding-compact"]
                  }`}
                  onClick={() => {
                    setShowShareDocument(true);
                    setCurrentDoc({
                      id: data[fileNo]?.documentId,
                      name: data[fileNo]?.documentName,
                    });
                  }}>
                  <div
                    className={`${
                      !onScreenComponent && "d-flex gap-2 align-items-center"
                    }`}>
                    <FaRegShareFromSquare size={20} />
                    {!onScreenComponent && <div>Share</div>}
                  </div>
                </button>
              </span>
            </Tooltip>

            <Tooltip anchorElement="target" position="bottom" parentTitle>
              <span title="AI Summary" className="d-inline-flex">
                <button
                  className={`me-2 ${styles["download-button"]} ${
                    !onScreenComponent
                      ? styles["button-padding-normal"]
                      : styles["button-padding-compact"]
                  }`}
                  onClick={() => {
                    setShowAISummary(true);
                    if (data.length && data[fileNo]) {
                      setCurrentDoc({
                        id: data[fileNo]?.documentId,
                        name: data[fileNo]?.documentName,
                        ...data[fileNo],
                      });
                    }
                  }}>
                  <div
                    className={`${
                      !onScreenComponent && "d-flex gap-2 align-items-center"
                    }`}>
                    <HiOutlineSparkles size={20} />
                    {!onScreenComponent && <div>AI Summary</div>}
                  </div>
                </button>
              </span>
            </Tooltip>
          </>
        )}
      </div>
    );
  };

  const DialogTitleBar = (titleText) => {
    return (
      <DialogTitleBarContent
        titleText={titleText}
        isStandalone={isStandalone}
        matterRef={matterRef}
        isEmpty={isEmpty}
        onClose={onClose}
        fullScreen={fullScreen}
        setFullScreen={setFullScreen}
        handleDocumentRefresh={handleDocumentRefresh}
        styles={styles}
      />
    );
  };

  return onScreenComponent || showShareDocument ? (
    <div className="w-100 h-100 position-relative d-flex flex-column">
      {loadingScreen && (
        <Loader
          themeColor={"warning"}
          size={"large"}
          type={"pulsing"}
          style={{
            position: "absolute",
            top: "45%",
            left: "40%",
            zIndex: 100,
          }}
        />
      )}
      {actionBar && (
        <div
          className={`d-flex justify-content-between align-items-center ${styles["action-bar"]}`}>
          {OnlyRefreshEditIcon ? (
            <span className="ms-2 ">{""}</span>
          ) : (
            <span className="ms-2 ">{getFileToolBarData()}</span>
          )}
          <div>
            <div className="d-flex align-items-center">
              {OnlyRefreshEditIcon ? (
                <>
                  {!isEmpty && (
                    <Tooltip anchorElement="target" position="bottom" parentTitle>
                      <span title="Refresh" className="d-inline-flex">
                        <IconBackground onClick={handleDocumentRefresh}>
                          <SvgIcon
                            icon={refreshIcon}
                            style={{ width: 17, height: 17 }}
                          />
                        </IconBackground>
                      </span>
                    </Tooltip>
                  )}
                  {!isEmpty &&
                    (wordExt.includes(
                      fileName[fileNo]?.split(".").pop()?.toLowerCase()
                    ) ||
                      excelExt.includes(
                        fileName[fileNo]?.split(".").pop()?.toLowerCase()
                      )) && (
                      <Tooltip anchorElement="target" position="bottom" parentTitle>
                        <span title="Edit" className="d-inline-flex">
                          <SplitButton
                            fillMode={"flat"}
                            className={`me-2 ${styles["spliter-class"]}`}
                            text={
                              <div className="d-flex gap-2 align-items-center">
                                <LiaEditSolid size={22} />
                                {!onScreenComponent && <div>Edit</div>}
                              </div>
                            }
                            items={getEditButtonItems()}
                          />
                        </span>
                      </Tooltip>
                    )}
                </>
              ) : (
                !iconLess && (
                  <>
                    {!isEmpty && (
                      <>
                        <Tooltip anchorElement="target" position="bottom" parentTitle>
                          <span title="Refresh" className="d-inline-flex">
                            <IconBackground onClick={handleDocumentRefresh}>
                              <SvgIcon
                                icon={refreshIcon}
                                style={{ width: 17, height: 17 }}
                              />
                            </IconBackground>
                          </span>
                        </Tooltip>
                        <hr className={`${styles["vertical-border"]} ms-3 me-3`} />
                      </>
                    )}
                    <Tooltip anchorElement="target" position="bottom" parentTitle>
                      <span title="Full screen" className="d-inline-flex">
                        <IconBackground onClick={onExpand}>
                          <BiExpand size={20} role="button" />
                        </IconBackground>
                      </span>
                    </Tooltip>
                    <hr className={`${styles["vertical-border"]} ms-3 me-3`} />
                    {onOpenInNewTab && !isEmpty && (
                      <>
                        <Tooltip anchorElement="target" position="bottom" parentTitle>
                          <span title="Open in new tab" className="d-inline-flex">
                            <IconBackground
                              onClick={() =>
                                onOpenInNewTab(
                                  data[fileNo]?.documentId,
                                  data[fileNo]?.documentName
                                )
                              }>
                              <img
                                src={NewTabIcon}
                                alt="Open in new tab"
                                width={20}
                                height={20}
                              />
                            </IconBackground>
                          </span>
                        </Tooltip>
                        <hr className={`${styles["vertical-border"]} ms-3 me-3`} />
                      </>
                    )}
                  </>
                )
              )}
              {!OnlyRefreshEditIcon && (
                <Tooltip anchorElement="target" position="bottom" parentTitle>
                  <span title="Close" className="d-inline-flex">
                    <IconBackground onClick={onClose} className="me-2">
                      <RxCross2 size={20} color="red" role="button" />
                    </IconBackground>
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={`flex-grow-1 overflow-hidden ${styles["viewer-content"]}`}>
        {isEmpty ? <EmptyDocument /> : renderFile()}
      </div>

      {showShareDocument && (
        <ShareDocumentModal
          onClose={() => {
            setShowShareDocument(false);
            if (component !== "caseActivities" && !isStandalone) {
              onClose();
            }
          }}
          matterId={matterId}
          matter={matter}
          currentDoc={currentDoc}
        />
      )}

      {showAISummary && (
        <AISummaryModal
          title={`AI Summary - ${currentDoc?.name || "Document"}`}
          onClose={() => {
            setShowAISummary(false);
          }}
          currentDoc={currentDoc}
          activityType={activityType}
          details={details}
        />
      )}

    </div>
  ) : (
    <>
      <div className="pdfwindow">
        <Dialog
          className={` dialog pdfwindow fs-md ${
            isStandalone && styles["is-standalone"]
          } ${fullScreen ? " dialog-fullscreen " : " dialog-xxl "}`}
          title={DialogTitleBar(fileName[fileNo])}
          onClose={() => {
            onClose();
          }}>
          {loadingScreen && (
            <Loader
              themeColor={"warning"}
              size={"large"}
              type={"pulsing"}
              style={{
                position: "absolute",
                top: "40%",
                left: "45%",
                zIndex: 100,
              }}
            />
          )}

          <div className={`d-flex gap-3 px-3 ${styles["dialog-content-height"]}`}>
            <img
              className={`leftside-collapse-button ${
                collapsedVersion
                  ? styles["collapse-button-collapsed"]
                  : styles["collapse-button-expanded"]
              } ${styles["collapse-button-top"]}`}
              src={collapsedVersion ? openSideBar : closeSideBar}
              onClick={() => {
                setCollapsedVersion(!collapsedVersion);
              }}
              alt="Collapse Icon"></img>
            <Tile
              heading={
                collapsedVersion ? (
                  <TbVersionsFilled className="ms-3" size={24} />
                ) : (
                  "Versions"
                )
              }
              className="px-3 py-2 mt-2 overflow-hidden"
              height="97%"
              width={collapsedVersion ? "100px" : "220px"}>
              {versions.map((version, index) => (
                <VersionCard
                  key={version.fileID}
                  version={version}
                  index={index}
                  collapsedVersion={collapsedVersion}
                  changeVersion={(version) => {
                    setCurrentVersion(index);
                    showDocumentFile(version?.fileID, true);
                  }}
                  currentVersion={currentVersion}
                  isLatest={index === 0}
                />
              ))}
            </Tile>
            {renderFile()}
          </div>

          <div id="footer" className="row dialog-footer m-0 float-none">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <div className={`ms-3 me-2 ${styles["footer-text"]}`}>
                  Previewing {fileNo + 1} of {fileName.length}
                </div>
                {!hideToolbar && (
                  <div className="d-flex align-items-center">
                    <div className={`ms-4 me-3 ${styles["vertical-border"]}`}></div>
                    {getFileToolBarData()}
                  </div>
                )}
              </div>
              <div>
                <div className="d-flex align-items-center">
                  <button
                    className={`me-2 ${styles["nav-button"]}`}
                    disabled={fileNo === 0}
                    onClick={() => handleFileNo("PREV")}>
                    <IoCaretBack fontSize={20} />
                  </button>
                  <button
                    className={`me-4 ${styles["nav-button"]}`}
                    disabled={fileName.length === fileNo + 1}
                    onClick={() => handleFileNo("NEXT")}>
                    <IoCaretForward fontSize={20} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Dialog>
      </div>

      {showShareDocument && (
        <ShareDocumentModal
          onClose={() => {
            setShowShareDocument(false);
            onClose();
          }}
          matterId={matterId}
          matter={matter}
          currentDoc={currentDoc}
        />
      )}

      {showAISummary && (
        <AISummaryModal
          title={`AI Summary - ${currentDoc?.name || "Document"}`}
          onClose={() => {
            setShowAISummary(false);
          }}
          currentDoc={currentDoc}
          activityType={activityType}
          details={details}
        />
      )}

    </>
  );
}
